import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { BarChart3, Users, Eye, Clock, TrendingUp, Plus, X } from 'lucide-react';


import { API_URL } from '../../../config/api';
const API = API_URL;
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('lms_token')}`
});

export default function PollList({ user, onLogout }) {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newPoll, setNewPoll] = useState({
    question: '',
    poll_type: 'yes_no',
    options: [],
    is_active: true,
    target_audience: 'student'
  });
  const [customOption, setCustomOption] = useState({ value: '', label: '' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchPolls();
  }, []);

  const fetchPolls = async () => {
    try {
      const response = await axios.get(`${API}/polls`, {
        headers: getAuthHeaders()
      });
      setPolls(response.data);
    } catch (error) {
      console.error('Error fetching polls:', error);
    } finally {
      setLoading(false);
    }
  };

  const togglePollStatus = async (pollId, currentStatus) => {
    try {
      await axios.put(
        `${API}/polls/${pollId}/toggle`,
        {},
        { headers: getAuthHeaders() }
      );
      fetchPolls(); // Refresh list
    } catch (error) {
      console.error('Error toggling poll status:', error);
    }
  };

  const addCustomOption = () => {
    if (customOption.value && customOption.label) {
      setNewPoll({
        ...newPoll,
        options: [...newPoll.options, { ...customOption }]
      });
      setCustomOption({ value: '', label: '' });
    }
  };

  const removeOption = (index) => {
    setNewPoll({
      ...newPoll,
      options: newPoll.options.filter((_, i) => i !== index)
    });
  };

  const handleCreatePoll = async () => {
    if (!newPoll.question.trim()) {
      alert('Please enter a poll question');
      return;
    }

    if (newPoll.poll_type === 'multiple_choice' && newPoll.options.length === 0) {
      alert('Please add at least one option for multiple choice poll');
      return;
    }

    setCreating(true);
    try {
      await axios.post(
        `${API}/polls`,
        newPoll,
        { headers: getAuthHeaders() }
      );
      
      // Reset form and close modal
      setNewPoll({
        question: '',
        poll_type: 'yes_no',
        options: [],
        is_active: true,
        target_audience: 'student'
      });
      setShowCreateModal(false);
      
      // Refresh polls list
      fetchPolls();
      
      alert('Poll created successfully!');
    } catch (error) {
      console.error('Error creating poll:', error);
      alert('Failed to create poll. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Polls Management</h1>
            <p className="text-gray-600">View and manage all student polls</p>
          </div>
          
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all flex items-center gap-2 shadow-lg"
          >
            <Plus className="w-5 h-5" />
            Create New Poll
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm mb-1">Total Polls</p>
                <p className="text-3xl font-bold">{polls.length}</p>
              </div>
              <BarChart3 className="w-12 h-12 opacity-80" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm mb-1">Active Polls</p>
                <p className="text-3xl font-bold">{polls.filter(p => p.is_active).length}</p>
              </div>
              <TrendingUp className="w-12 h-12 opacity-80" />
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm mb-1">Total Responses</p>
                <p className="text-3xl font-bold">
                  {polls.reduce((sum, p) => sum + p.total_responses, 0)}
                </p>
              </div>
              <Users className="w-12 h-12 opacity-80" />
            </div>
          </div>
        </div>

        {/* Polls List */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800">All Polls</h2>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <p className="text-gray-600">Loading polls...</p>
            </div>
          ) : polls.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-600">No polls created yet</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {polls.map((poll) => (
                <div
                  key={poll.id}
                  className="p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                          Poll No. {poll.poll_number}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          poll.is_active 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {poll.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>

                      <h3 className="text-lg font-semibold text-gray-800 mb-2">
                        {poll.question}
                      </h3>

                      <div className="flex items-center gap-6 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          <span>{poll.total_responses} responses</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          <span>
                            {new Date(poll.created_at).toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => togglePollStatus(poll.id, poll.is_active)}
                        className={`px-4 py-2 rounded-lg font-medium transition-all ${
                          poll.is_active
                            ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                      >
                        {poll.is_active ? 'Deactivate' : 'Activate'}
                      </button>

                      <button
                        onClick={() => navigate(`/admin/polls/${poll.id}`)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all flex items-center gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        View Responses
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Poll Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Create New Poll</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-5">
              {/* Poll Question */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Poll Question <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newPoll.question}
                  onChange={(e) => setNewPoll({ ...newPoll, question: e.target.value })}
                  className="w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                  rows="3"
                  placeholder="e.g., Do you have your school exams in January?"
                />
              </div>

              {/* Poll Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Poll Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={newPoll.poll_type}
                  onChange={(e) => setNewPoll({ ...newPoll, poll_type: e.target.value, options: [] })}
                  className="w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  <option value="yes_no">Yes / No</option>
                  <option value="multiple_choice">Multiple Choice</option>
                  <option value="text">Text Response</option>
                </select>
              </div>

              {/* Multiple Choice Options */}
              {newPoll.poll_type === 'multiple_choice' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Options <span className="text-red-500">*</span>
                  </label>
                  
                  {/* Existing Options */}
                  {newPoll.options.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {newPoll.options.map((option, index) => (
                        <div key={index} className="flex items-center gap-2 bg-gray-50 p-3 rounded-lg">
                          <span className="flex-1 font-medium text-gray-800">{option.label}</span>
                          <button
                            onClick={() => removeOption(index)}
                            className="text-red-600 hover:text-red-700 p-1"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add New Option */}
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={customOption.value}
                      onChange={(e) => setCustomOption({ ...customOption, value: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                      placeholder="Option value (e.g., option_1)"
                      className="w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      value={customOption.label}
                      onChange={(e) => setCustomOption({ ...customOption, label: e.target.value })}
                      placeholder="Option label (e.g., Strongly Agree)"
                      className="w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={addCustomOption}
                      disabled={!customOption.value || !customOption.label}
                      className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      + Add Option
                    </button>
                  </div>
                </div>
              )}

              {/* Target Audience */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Target Audience <span className="text-red-500">*</span>
                </label>
                <select
                  value={newPoll.target_audience}
                  onChange={(e) => setNewPoll({ ...newPoll, target_audience: e.target.value })}
                  className="w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                >
                  <option value="student">Students</option>
                  <option value="teacher">Teachers</option>
                  <option value="mentor">Mentors</option>
                  <option value="all">Everyone</option>
                </select>
              </div>

              {/* Active Status */}
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newPoll.is_active}
                    onChange={(e) => setNewPoll({ ...newPoll, is_active: e.target.checked })}
                    className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="text-sm font-semibold text-gray-700">
                    Make poll active immediately (students will see it after login)
                  </span>
                </label>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t flex gap-3 sticky bottom-0">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleCreatePoll}
                disabled={creating || !newPoll.question.trim()}
                className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium shadow-lg"
              >
                {creating ? 'Creating...' : 'Create Poll'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
